import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PermissionService } from '@core/services/permission.service';
import { AccionPermiso } from '@shared/models/administracion/modulo.model';
import {
  type ColumnFilterValue,
  TableColumnConfig,
  TableBuilderConfig,
  TableRowActionButton,
  TableRowActionsConfig,
  TableRowDetailItem,
  TableRowExpansionConfig,
  TableStatusFilterOption,
} from './table-builder.model';
import { DropdownComponent, type DropdownOption } from '../dropdown';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const DEFAULT_CONFIG: Required<
  Omit<
    TableBuilderConfig,
    'statusFilterOptions' | 'statusFilterKey' | 'searchPlaceholder' | 'emptyMessage'
  >
> & {
  statusFilterOptions: TableStatusFilterOption[];
  statusFilterKey: string;
  searchPlaceholder: string;
  emptyMessage: string;
} = {
  showSearch: true,
  externalSearch: false,
  searchPlaceholder: 'Buscar...',
  showPerPage: true,
  pageSizeOptions: [5, 10, 25, 50],
  defaultPageSize: 10,
  showPagination: true,
  showStatusFilter: false,
  statusFilterOptions: [
    { value: true, label: 'Activo' },
    { value: false, label: 'Inactivo' },
  ],
  statusFilterKey: 'estado',
  showCreateButton: true,
  showRefreshButton: true,
  showColumnToggle: true,
  showColumnFilters: true,
  createButtonLabel: 'Crear',
  emptyMessage: 'No hay registros.',
  headerTone: 'default',
  permissionModule: '',
  createPermission: '',
  editPermission: '',
  deletePermission: '',
};

@Component({
  selector: 'app-table-builder',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideNativeDateAdapter()],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatMenuModule,
    DropdownComponent,
    MatTooltipModule,
    TranslatePipe,
  ],
  templateUrl: './table-builder.component.html',
  styleUrl: './table-builder.component.scss',
})
export class TableBuilderComponent<T extends object = object> {
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _langTick = signal(0);
  private readonly permissionService = inject(PermissionService);

  private normalizePermissionSegment(value: string): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .trim()
      .replace(/[\s.-]+/g, '_')
      .replace(/_+/g, '_');
  }

  private resolvePermissionTarget(raw: string): { modulo: string; subModulo: string } | null {
    const normalized = this.normalizePermissionSegment(raw);
    if (!normalized) return null;

    const parts = normalized
      .split('|')
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length >= 2) {
      return { modulo: parts[0], subModulo: parts[1] };
    }

    return { modulo: normalized, subModulo: normalized };
  }

readonly canCreate = computed(() => {
  const target = this.resolvePermissionTarget(this.mergedConfig().permissionModule);
  if (!target) return true;
  return this.permissionService.tienePermiso(target.modulo, target.subModulo, AccionPermiso.CREAR);
});

readonly canEdit = computed(() => {
  const target = this.resolvePermissionTarget(this.mergedConfig().permissionModule);
  if (!target) return true;
  return this.permissionService.tienePermiso(target.modulo, target.subModulo, AccionPermiso.EDITAR);
});

readonly canDelete = computed(() => {
  const target = this.resolvePermissionTarget(this.mergedConfig().permissionModule);
  if (!target) return true;
  return this.permissionService.tienePermiso(target.modulo, target.subModulo, AccionPermiso.ELIMINAR);
});

  private normalizeForSearch(value: unknown): string {

    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private buildSearchPlaceholderFromColumns(): string {
    const cols = this.columns?.() ?? [];
    const labels = cols
      .filter(
        (c) =>
          c?.searchable !== false &&
          c?.type !== 'iconOnly' &&
          // No sugerir columnas ocultas por defecto (p. ej. IDs internos).
          c?.visible !== false &&
          // El % de avance no suele ser un criterio útil en el buscador global.
          c?.type !== 'progress'
      )
      .map((c) => String(c?.label ?? '').trim())
      .filter((l) => l.length > 0)
      // Evitar duplicados manteniendo el orden
      .filter((l, idx, arr) => arr.indexOf(l) === idx);

    if (labels.length === 0) {
      return this.translate.instant('shared.tableBuilder.defaultSearchPlaceholder');
    }

    const max = 4;
    const shown = labels.slice(0, max);
    return this.translate.instant('shared.tableBuilder.searchByColumns', {
      columns: shown.join(', '),
    });
  }
  /** Datos a mostrar (array de registros) */
  data = input.required<T[]>();

  trackByFn = (index: number, row: any) => {
    // Prefer stable entity ids and allow optional row versioning to refresh a single row.
    const baseId = (
      row?.pagoTrabajadorId ??
      row?.id ??
      row?.postulacionId ??
      row?.capacitacionId ??
      row?.periodoVacacionId ??
      row?.trabajadorId ??
      index
    );
    const rowVersion = row?._uiVersion;
    return rowVersion !== undefined ? `${baseId}:${rowVersion}` : baseId;
  };

  /** Definición de columnas */
  columns = input.required<TableColumnConfig<T>[]>();

  /** Configuración de la tabla (buscador, paginación, etc.) */
  config = input<TableBuilderConfig>({});

  /**
   * Token opcional para forzar el reinicio de la paginación a la primera página.
   * Incrementar/cambiar desde el padre cuando se requiera volver al inicio.
   */
  resetPageToken = input<unknown>(null);

  /** Acciones por fila (ver, editar, eliminar) */
  rowActions = input<TableRowActionsConfig>({
    showView: true,
    showEdit: true,
    showDelete: true,
  });

  /** Botones de acción extra por fila (ej. Inactivar) */
  extraRowActions = input<TableRowActionButton<T>[]>([]);
  maxInlineActions = input<number>(3);
  /** Configuración opcional para expansión por fila */
  rowExpansion = input<TableRowExpansionConfig<T> | null>(null);

  showToolbarExtra = input<boolean>(false);
  showSelection = input<boolean>(false);
  selectedIds = input<Set<any>>(new Set());
  selectionChange = output<Set<any>>();

  /** Emitido al pulsar "Crear" */
  create = output<void>();

  /** Emitido al pulsar Ver en una fila */
  view = output<T>();

  /** Emitido al pulsar Editar en una fila */
  edit = output<T>();

  /** Emitido al pulsar Eliminar en una fila */
  delete = output<T>();

  /** Emitido al pulsar Recargar datos */
  refresh = output<void>();

  /** Emitido cuando cambia el buscador global (útil para búsqueda server-side) */
  searchTermChange = output<string>();

  /** Emitido al pulsar un botón de acción extra; payload: { actionId, row } */
  customAction = output<{ actionId: string; row: T }>();

  /** Emitido al pulsar la celda de columna CV (iconOnly con key `cv`) */
  cvClick = output<T>();

  /** Emitido al pulsar un botón de acción dentro del detalle expandido */
  detailAction = output<{ actionId: string; row: T; detail: TableRowDetailItem }>();

  searchTerm = signal('');
  pageSize = signal(DEFAULT_CONFIG.defaultPageSize);
  currentPage = signal(1);
  statusFilterValue = signal<string | boolean | number | null>(null);
  /** Orden: key de columna o null = sin orden */
  sortColumnKey = signal<string | null>(null);
  /** Dirección: 'asc' | 'desc' */
  sortDirection = signal<'asc' | 'desc'>('asc');
  /** Claves de columnas ocultas por el usuario (toggle columnas) */
  hiddenColumnKeys = signal<Set<string>>(new Set());
  /** Filtros por columna (key → valor según tipo: string, { min, max }, boolean | null) */
  columnFilters = signal<Record<string, ColumnFilterValue>>({});
  /** Panel de filtros por columna: visible/oculto debajo del toolbar */
  filtersPanelOpen = signal(false);

  /** Vista móvil (handset) para mostrar menú de acciones en lugar de botones sueltos */
  isMobileView = toSignal(
    inject(BreakpointObserver).observe(Breakpoints.Handset).pipe(map((r) => r.matches)),
    { initialValue: false }
  );

  /** Sincronizar pageSize inicial con config.defaultPageSize (solo una vez) */
  private hasInitializedPageSize = signal(false);
  /** Inicializar hiddenColumnKeys con columnas que tienen visible=false (solo una vez) */
  private hasInitializedHiddenKeys = signal(false);

  constructor() {
    effect(() => {
      if (this.hasInitializedPageSize()) return;
      this.data(); // esperar a que el padre haya pasado data (y normalmente config)
      const defaultSize = this.mergedConfig().defaultPageSize;
      this.pageSize.set(defaultSize);
      this.hasInitializedPageSize.set(true);
    });
    effect(() => {
      if (this.hasInitializedHiddenKeys()) return;
      const cols = this.columns();
      if (cols.length === 0) return;
      const initiallyHidden = new Set(cols.filter((c) => c.visible === false).map((c) => c.key));
      if (initiallyHidden.size > 0) {
        this.hiddenColumnKeys.set(initiallyHidden);
      }
      this.hasInitializedHiddenKeys.set(true);
    });

    effect(() => {
      this.resetPageToken();
      this.currentPage.set(1);
    });

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this._langTick.update((x) => x + 1);
    });
  }

  /** Opciones para el dropdown de estado: Todos + Activo/Inactivo */
  statusFilterDropdownOptions = computed<DropdownOption<string | boolean | number | null>[]>(() => {
    this._langTick();
    return [
      { value: null, label: this.translate.instant('shared.tableBuilder.all') },
      ...this.mergedConfig().statusFilterOptions,
    ];
  });

  /** Opciones para el dropdown "Por página" */
  pageSizeDropdownOptions = computed<DropdownOption<number>[]>(() =>
    this.mergedConfig().pageSizeOptions.map((n) => ({ value: n, label: String(n) }))
  );

  allSelected = computed(() => {
    const data = this.paginatedData();
    if (data.length === 0) return false;
    const selected = this.selectedIds();
    return data.every((row) => selected.has(this.getRowId(row)));
  });

  someSelected = computed(() => {
    const data = this.paginatedData();
    const selected = this.selectedIds();
    const count = data.filter((row) => selected.has(this.getRowId(row))).length;
    return count > 0 && count < data.length;
  });

  /** Opciones para filtros booleanos por columna */
  readonly booleanFilterOptions = computed<DropdownOption<boolean | null>[]>(() => {
    this._langTick();
    return [
      { value: null, label: this.translate.instant('shared.tableBuilder.all') },
      { value: true, label: this.translate.instant('shared.tableBuilder.yes') },
      { value: false, label: this.translate.instant('shared.tableBuilder.no') },
    ];
  });

  public mergedConfig = computed(() => {
    this._langTick();
    const c = this.config();
    const resolvedSearchPlaceholder =
      c?.searchPlaceholder ?? this.buildSearchPlaceholderFromColumns();
    return {
      ...DEFAULT_CONFIG,
      ...c,
      showCreateButton: c?.showCreateButton ?? DEFAULT_CONFIG.showCreateButton,
      statusFilterOptions: c?.statusFilterOptions ?? DEFAULT_CONFIG.statusFilterOptions,
      statusFilterKey: c?.statusFilterKey ?? DEFAULT_CONFIG.statusFilterKey,
      searchPlaceholder: resolvedSearchPlaceholder,
      toggleableColumns: this.columns(),
    };
  });

  /** Columnas visibles: las que no están en hiddenColumnKeys (incluye las que tenían visible=false si el usuario las muestra) */
  visibleColumns = computed(() => {
    const hidden = this.hiddenColumnKeys();
    return this.columns().filter((col) => !hidden.has(col.key));
  });

  /** Todas las columnas para el menú de mostrar/ocultar (así el usuario puede mostrar las que inician con visible=false) */
  toggleableColumns = computed(() => this.columns());

  /** Columnas que tienen filtro habilitado (filterable=true) */
  filterableColumns = computed(() =>
    this.columns().filter((col) => col.filterable === true)
  );

  filteredData = computed(() => {
    const rows = this.data();
    const term = this.normalizeForSearch(this.searchTerm());
    const statusKey = this.mergedConfig().statusFilterKey;
    const statusVal = this.statusFilterValue();
    const filters = this.columnFilters();
    const cols = this.columns();

    let result = rows;

    if (statusVal !== null && statusVal !== undefined && statusKey) {
      result = result.filter((row) => {
        const v = this.getCellValue(row, statusKey);
        return v === statusVal || String(v) === String(statusVal);
      });
    }

    // Filtros por columna (fecha, número, texto, boolean, tiempo)
    for (const [colKey, filterValue] of Object.entries(filters)) {
      if (!this.hasFilterValue(filterValue)) continue;
      const col = cols.find((c) => c.key === colKey);
      if (!col) continue;
      result = result.filter((row) =>
        this.matchesColumnFilter(row, colKey, filterValue, col)
      );
    }

    // Si el padre maneja la búsqueda (server-side o filtrado externo), no aplicar filtro por término aquí.
    if (this.mergedConfig().externalSearch) return result;

    if (!term) return result;

    // Solo buscar en columnas con searchable: true (por defecto true si no está definido)
    const searchableCols = cols.filter((col) => col.searchable !== false);

    return result.filter((row) =>
      searchableCols.some((col) => {
        // Usamos el valor mostrado (formatter incluido) para que la búsqueda sea consistente con lo que ve el usuario.
        const display = this.getDisplayValue(row, col);
        return this.normalizeForSearch(display).includes(term);
      })
    );
  });

  /** Datos filtrados y ordenados (para paginación y filas) */
  sortedData = computed(() => {
    const list = [...this.filteredData()];
    const sortKey = this.sortColumnKey();
    const dir = this.sortDirection();
    const cols = this.columns();

    if (!sortKey || !dir) return list;

    const col = cols.find((c) => c.key === sortKey);
    const key = col?.sortKey ?? col?.key ?? sortKey;

    return list.sort((a, b) => {
      const va = this.getCellValue(a, key);
      const vb = this.getCellValue(b, key);
      if (col?.type === 'progress') {
        const pa = this.parsePercentValue(va);
        const pb = this.parsePercentValue(vb);
        const cmp = pa - pb;
        return dir === 'asc' ? cmp : -cmp;
      }
      if (col?.type === 'date') {
        const ta = va != null ? new Date(va as string | number | Date).getTime() : NaN;
        const tb = vb != null ? new Date(vb as string | number | Date).getTime() : NaN;
        const safeA = Number.isNaN(ta) ? 0 : ta;
        const safeB = Number.isNaN(tb) ? 0 : tb;
        const cmp = safeA - safeB;
        return dir === 'asc' ? cmp : -cmp;
      }
      const aNum = typeof va === 'number';
      const bNum = typeof vb === 'number';
      const aStr = String(va ?? '').toLowerCase();
      const bStr = String(vb ?? '').toLowerCase();
      const cmp =
        aNum && bNum
          ? (va as number) - (vb as number)
          : aStr.localeCompare(bStr, undefined, { numeric: true });
      return dir === 'asc' ? cmp : -cmp;
    });
  });

  paginatedData = computed(() => {
    const list = this.sortedData();
    const size = this.pageSize();
    const page = this.currentPage();
    const start = (page - 1) * size;
    return list.slice(start, start + size);
  });

  totalPages = computed(() => {
    const total = this.sortedData().length;
    const size = this.pageSize();
    return Math.max(1, Math.ceil(total / size));
  });

  /** Texto "X–Y de Z" para la barra de paginación */
  paginationRange = computed(() => {
    const total = this.sortedData().length;
    const size = this.pageSize();
    const page = this.currentPage();
    const start = (page - 1) * size + 1;
    const end = Math.min(page * size, total);
    return { start, end, total };
  });

  hasActions = computed(() => {
    const ra = this.rowActions();
    const extra = this.extraRowActions();
    return !!(ra?.showView || ra?.showEdit || ra?.showDelete || extra?.length > 0);
  });

  /** Fila actual para el menú de acciones (cuando hay > 3 acciones). */
  actionsMenuRow = signal<T | null>(null);
  /** Fila actualmente expandida (solo una a la vez). */
  expandedRow = signal<T | null>(null);

  /** Cuenta de acciones visibles para una fila (Ver + Editar + Eliminar + extras visibles). */
  getVisibleActionsCount(row: T): number {
    const ra = this.rowActions();
    let n = 0;
    if (ra?.showView) n++;
    if (ra?.showEdit) n++;
    if (ra?.showDelete) n++;
    for (const action of this.extraRowActions()) {
      if (!action.visible || action.visible(row)) n++;
    }
    return n;
  }

  /** Asigna la fila de contexto antes de abrir el menú de acciones. */
  setActionsMenuRow(row: T): void {
    this.actionsMenuRow.set(row);
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.currentPage.set(1);
    this.searchTermChange.emit(value);
  }

  onPageSizeChange(size: number): void {
    if (size != null) {
      this.pageSize.set(size);
      this.currentPage.set(1);
    }
  }

  goToPage(page: number): void {
    const total = this.totalPages();
    this.currentPage.set(Math.max(1, Math.min(page, total)));
  }

  onStatusFilterChange(value: string | boolean | number | null): void {
    if (value === 'true') value = true;
    if (value === 'false') value = false;
    this.statusFilterValue.set(value);
    this.currentPage.set(1);
  }

  /** Alterna orden por columna: sin orden -> asc -> desc -> sin orden. */
  toggleSort(col: TableColumnConfig<T>): void {
    if (col.sortable !== true) return;
    const key = col.key;
    const current = this.sortColumnKey();
    const dir = this.sortDirection();
    if (current === key) {
      if (dir === 'asc') {
        this.sortDirection.set('desc');
      } else {
        this.sortColumnKey.set(null);
        this.sortDirection.set('asc');
      }
    } else {
      this.sortColumnKey.set(key);
      this.sortDirection.set('asc');
    }
  }

  isColumnSorted(col: TableColumnConfig<T>): boolean {
    return this.sortColumnKey() === col.key;
  }

  getSortIcon(col: TableColumnConfig<T>): 'asc' | 'desc' | null {
    if (this.sortColumnKey() !== col.key) return null;
    return this.sortDirection();
  }

  /** Nombre del icono Material para ordenación (un solo mat-icon, sin duplicados). */
  getSortIconMatName(col: TableColumnConfig<T>): string {
    const dir = this.getSortIcon(col);
    if (dir === 'asc') return 'arrow_upward';
    if (dir === 'desc') return 'arrow_downward';
    return 'unfold_more';
  }

  getCellValue(row: T, key: string): unknown {
    // Si el key completo existe directamente (ej: claves con puntos como "D.L.19990"),
    // retornarlo sin dividir para evitar falsa navegación de path anidado.
    if (row && typeof row === 'object' && key in (row as object)) {
      return (row as Record<string, unknown>)[key];
    }
    const parts = key.split('.');
    let val: unknown = row;
    for (const p of parts) {
      val = val && typeof val === 'object' && p in val ? (val as Record<string, unknown>)[p] : undefined;
    }
    return val;
  }

  getDisplayValue(row: T, column: TableColumnConfig<T>): string {
    const raw = this.getCellValue(row, column.key);
    if (column.formatter) return column.formatter(raw, row);
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'boolean') return raw ? 'Sí' : 'No';
    return String(raw);
  }

  /** Porcentaje 0–100 para columnas `type: 'progress'` (ej. "73%"). */
  getProgressPercent(row: T, column: TableColumnConfig<T>): number {
    return this.parsePercentValue(this.getCellValue(row, column.key));
  }

  private parsePercentValue(value: unknown): number {
    const s = String(value ?? '')
      .replace('%', '')
      .trim();
    const n = Number(s);
    if (!Number.isFinite(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }

  emitCreate(): void {
    this.create.emit();
  }

  getViewActionLabel(row: T): string {
    const ra = this.rowActions();
    if (ra.resolveViewActionLabel) {
      return ra.resolveViewActionLabel(row);
    }
    return ra.viewActionLabel ?? this.translate.instant('shared.tableBuilder.view');
  }

  emitView(row: T): void {
    this.view.emit(row);
  }

  emitEdit(row: T): void {
    this.edit.emit(row);
  }

  emitDelete(row: T): void {
    this.delete.emit(row);
  }

  emitCustomAction(actionId: string, row: T): void {
    this.customAction.emit({ actionId, row });
  }

  emitRefreshData(): void {
    this.refresh.emit();
  }

  getRowId(row: T): any {
    return (
      (row as any).idTrabajador ??
      (row as any).id ??
      (row as any).trabajadorId ??
      (row as any).trabajador?.idTrabajador ??
      (row as any).trabajador?.id ??
      this.sortedData().indexOf(row)
    );
  }

  toggleSelectAll(): void {
    const currentSelected = new Set(this.selectedIds());
    const data = this.paginatedData();

    if (this.allSelected()) {
      data.forEach((row) => currentSelected.delete(this.getRowId(row)));
    } else {
      data.forEach((row) => currentSelected.add(this.getRowId(row)));
    }

    this.selectionChange.emit(currentSelected);
  }

  toggleSelectRow(row: T): void {
    const id = this.getRowId(row);
    const currentSelected = new Set(this.selectedIds());

    if (currentSelected.has(id)) {
      currentSelected.delete(id);
    } else {
      currentSelected.add(id);
    }

    this.selectionChange.emit(currentSelected);
  }

  isRowSelected(row: T): boolean {
    return this.selectedIds().has(this.getRowId(row));
  }

  isRowExpansionEnabled(): boolean {
    const config = this.rowExpansion();
    return !!config?.enabled && !!config.triggerColumnKey && typeof config.detailsResolver === 'function';
  }

  /**
   * Colspan de la fila de detalle (columnas de datos + acciones + selección + control de expansión).
   */
  getExpandedDetailColspan(): number {
    return (
      this.visibleColumns().length +
      (this.hasActions() ? 1 : 0) +
      (this.showSelection() ? 1 : 0) +
      (this.isRowExpansionEnabled() ? 1 : 0)
    );
  }

  canToggleRowExpansion(col: TableColumnConfig<T>): boolean {
    const config = this.rowExpansion();
    if (!this.isRowExpansionEnabled() || !config) {
      return false;
    }

    return config.triggerColumnKey === '*' || col.key === config.triggerColumnKey;
  }

  toggleRowExpansion(row: T): void {
    this.expandedRow.set(this.expandedRow() === row ? null : row);
  }

  isRowExpanded(row: T): boolean {
    return this.expandedRow() === row;
  }

  getExpandedRowDetails(row: T): TableRowDetailItem[] {
    const config = this.rowExpansion();
    if (!this.isRowExpansionEnabled() || !config) {
      return [];
    }
    return config.detailsResolver(row) ?? [];
  }

  hasExpandedRowDetailActions(row: T): boolean {
    return this.getExpandedRowDetails(row).some((d) => !!d.actionLabel && !!d.actionId);
  }

  getRowExpansionHeaders(): { tipo: string; codigo: string; descripcion: string; accion?: string } {
    const cfg = this.rowExpansion();
    return (
      cfg?.headers ?? {
        tipo: 'Tipo',
        codigo: 'Codigo',
        descripcion: 'Descripción',
        accion: 'Acción',
      }
    );
  }

  getRowExpansionRenderMode(): 'table' | 'list' | 'chips' {
    return this.rowExpansion()?.renderMode ?? 'table';
  }

  getRowExpansionListTitle(): string {
    return (
      this.rowExpansion()?.listTitle ??
      this.translate.instant('shared.tableBuilder.listTitleDefault')
    );
  }

  emitDetailAction(actionId: string, row: T, detail: TableRowDetailItem): void {
    this.detailAction.emit({ actionId, row, detail });
  }

  /** Indica si la columna está visible (no está en hiddenColumnKeys). */
  isColumnVisible(colKey: string): boolean {
    return !this.hiddenColumnKeys().has(colKey);
  }

  /** Conmuta la visibilidad de una columna. */
  toggleColumnVisibility(colKey: string): void {
    const set = new Set(this.hiddenColumnKeys());
    if (set.has(colKey)) {
      set.delete(colKey);
    } else {
      set.add(colKey);
    }
    this.hiddenColumnKeys.set(set);
  }

  /** Tipo de filtro para la columna (filterType ?? type, para UI de filtros). */
  getColumnFilterType(col: TableColumnConfig<T>): string {
    const t = col.filterType ?? col.type ?? 'text';
    if (t === 'badge' || t === 'progress') return 'text';
    return t;
  }

  /** Opciones de combobox para filtro select (incluye "Todos"). */
  columnFilterSelectOptions(col: TableColumnConfig<T>): DropdownOption<string | null>[] {
    const opts = col.filterSelectOptions ?? [];
    return [{ value: null, label: 'Todos' }, ...opts.map((o) => ({ value: o.value, label: o.label }))];
  }

  getColumnFilterSelectValue(colKey: string): string | null {
    const v = this.columnFilters()[colKey];
    if (typeof v === 'string' && v.trim() !== '') return v;
    return null;
  }

  onColumnFilterSelectChange(colKey: string, value: string | null | undefined): void {
    if (value === null || value === undefined || value === '') {
      this.setColumnFilter(colKey, undefined);
      return;
    }
    this.setColumnFilter(colKey, value);
  }

  getColumnFilterMinAsDate(colKey: string): Date | null {
    const s = this.getColumnFilterMinStr(colKey);
    if (!s) return null;
    const parts = s.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
    const [y, m, d] = parts;
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  getColumnFilterMaxAsDate(colKey: string): Date | null {
    const s = this.getColumnFilterMaxStr(colKey);
    if (!s) return null;
    const parts = s.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
    const [y, m, d] = parts;
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  onColumnFilterDatePick(colKey: string, bound: 'min' | 'max', value: Date | null | undefined): void {
    if (value == null || !(value instanceof Date) || Number.isNaN(value.getTime())) {
      this.setColumnFilterRange(colKey, bound, undefined);
      return;
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    this.setColumnFilterRange(colKey, bound, `${y}-${m}-${d}`);
  }

  private parseCellDateForFilter(val: unknown): number | null {
    if (val == null || val === '') return null;
    if (val instanceof Date && !Number.isNaN(val.getTime())) {
      const x = val;
      return new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    }
    const s = String(val).trim();
    const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy) {
      const day = parseInt(dmy[1], 10);
      const month = parseInt(dmy[2], 10) - 1;
      const year = parseInt(dmy[3], 10);
      const dt = new Date(year, month, day);
      if (!Number.isNaN(dt.getTime())) return dt.getTime();
    }
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const dt = new Date(parseInt(iso[1], 10), parseInt(iso[2], 10) - 1, parseInt(iso[3], 10));
      if (!Number.isNaN(dt.getTime())) return dt.getTime();
    }
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
    }
    return null;
  }

  /** Indica si hay un valor de filtro aplicado. */
  hasFilterValue(value: ColumnFilterValue | undefined): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (typeof value === 'boolean') return true;
    if (typeof value === 'object' && value !== null) {
      const r = value as { min?: unknown; max?: unknown };
      return r.min !== undefined && r.min !== '' || r.max !== undefined && r.max !== '';
    }
    return false;
  }

  /** Obtiene el valor de filtro de una columna. */
  getColumnFilter(colKey: string): ColumnFilterValue | undefined {
    return this.columnFilters()[colKey];
  }

  /** Obtiene min de un filtro tipo rango (date, time, number). */
  getColumnFilterMin(colKey: string): string | number | undefined {
    const v = this.columnFilters()[colKey];
    return v != null && typeof v === 'object' && 'min' in v ? (v as { min?: string | number }).min : undefined;
  }

  /** Obtiene max de un filtro tipo rango. */
  getColumnFilterMax(colKey: string): string | number | undefined {
    const v = this.columnFilters()[colKey];
    return v != null && typeof v === 'object' && 'max' in v ? (v as { max?: string | number }).max : undefined;
  }

  /** Valor string de filtro para binding en inputs (evita 'as' en template). */
  getColumnFilterStr(colKey: string): string {
    const v = this.getColumnFilter(colKey);
    return typeof v === 'string' ? v : '';
  }

  /** Valor string de min para binding en inputs date/time. */
  getColumnFilterMinStr(colKey: string): string {
    const v = this.getColumnFilterMin(colKey);
    return v != null && v !== '' ? String(v) : '';
  }

  /** Valor string de max para binding en inputs date/time. */
  getColumnFilterMaxStr(colKey: string): string {
    const v = this.getColumnFilterMax(colKey);
    return v != null && v !== '' ? String(v) : '';
  }

  /** Actualiza filtro tipo rango (min/max) para date, time o number. */
  setColumnFilterRange(colKey: string, type: 'min' | 'max', value: string | number | undefined): void {
    const current = this.columnFilters()[colKey];
    const prev = (current != null && typeof current === 'object' && !Array.isArray(current))
      ? { ...(current as Record<string, unknown>) }
      : {};
    if (value === undefined || value === null || value === '') {
      delete prev[type];
    } else {
      prev[type] = value;
    }
    const hasAny = ('min' in prev && prev['min'] != null && prev['min'] !== '') || ('max' in prev && prev['max'] != null && prev['max'] !== '');
    this.setColumnFilter(colKey, hasAny ? (prev as ColumnFilterValue) : undefined);
  }

  /** Valor para el dropdown de filtro booleano: true, false o null. */
  getColumnFilterBooleanValue(colKey: string): boolean | null {
    const v = this.columnFilters()[colKey];
    if (v === true || v === false) return v;
    return null;
  }

  /** Valor para binding boolean filter (select). */
  getColumnFilterBooleanSelect(colKey: string): string {
    const v = this.columnFilters()[colKey];
    if (v === true) return 'true';
    if (v === false) return 'false';
    return '';
  }

  /** Handler para cambio de filtro boolean. */
  onColumnFilterBooleanChange(colKey: string, value: string): void {
    const val = value === '' ? null : value === 'true';
    this.setColumnFilter(colKey, val);
  }

  /** Asigna el valor de filtro de una columna y vuelve a página 1. */
  setColumnFilter(colKey: string, value: ColumnFilterValue | undefined): void {
    const next = { ...this.columnFilters() };
    if (value === undefined || value === null || value === '' ||
        (typeof value === 'object' && 'min' in value && value.min === undefined && value.max === undefined)) {
      delete next[colKey];
    } else {
      next[colKey] = value;
    }
    this.columnFilters.set(next);
    this.currentPage.set(1);
  }

  /** Limpia todos los filtros por columna. */
  clearColumnFilters(): void {
    this.columnFilters.set({});
    this.currentPage.set(1);
  }

  toggleFiltersPanel(): void {
    this.filtersPanelOpen.update((v) => !v);
  }

  /** Indica si la fila cumple el filtro de la columna. */
  matchesColumnFilter(
    row: T,
    colKey: string,
    filterValue: ColumnFilterValue,
    col: TableColumnConfig<T>
  ): boolean {
    const cellVal = this.getCellValue(row, col.key);
    const filterType = this.getColumnFilterType(col);

    switch (filterType) {
      case 'text':
        return this.normalizeForSearch(cellVal).includes(this.normalizeForSearch(filterValue as string));
      case 'select': {
        const f = filterValue as string;
        if (f === null || f === undefined || String(f).trim() === '') return true;
        return this.normalizeForSearch(cellVal) === this.normalizeForSearch(f);
      }
      case 'boolean':
        return cellVal === filterValue;
      case 'number':
      case 'currency': {
        const num = typeof cellVal === 'number' ? cellVal : Number(cellVal);
        if (Number.isNaN(num)) return false;
        const r = filterValue as { min?: number | string; max?: number | string };
        const minVal = r.min != null && r.min !== '' ? Number(r.min) : NaN;
        const maxVal = r.max != null && r.max !== '' ? Number(r.max) : NaN;
        if (!Number.isNaN(minVal) && num < minVal) return false;
        if (!Number.isNaN(maxVal) && num > maxVal) return false;
        return true;
      }
      case 'date': {
        const r = filterValue as { min?: string; max?: string };
        const cellTime = this.parseCellDateForFilter(cellVal);
        if (cellTime === null) return false;
        if (r.min) {
          const p = r.min.split('-').map(Number);
          if (p.length === 3 && !p.some((n) => Number.isNaN(n))) {
            const minDate = new Date(p[0], p[1] - 1, p[2]).getTime();
            if (cellTime < minDate) return false;
          }
        }
        if (r.max) {
          const p = r.max.split('-').map(Number);
          if (p.length === 3 && !p.some((n) => Number.isNaN(n))) {
            const maxDate = new Date(p[0], p[1] - 1, p[2], 23, 59, 59, 999).getTime();
            if (cellTime > maxDate) return false;
          }
        }
        return true;
      }
      case 'time': {
        const r = filterValue as { min?: string; max?: string };
        const cellMinutes = this.parseTimeToMinutes(cellVal);
        if (cellMinutes === null) return false;
        const minM = r.min ? this.parseTimeStringToMinutes(r.min) : null;
        const maxM = r.max ? this.parseTimeStringToMinutes(r.max) : null;
        if (minM !== null && cellMinutes < minM) return false;
        if (maxM !== null && cellMinutes > maxM) return false;
        return true;
      }
      default:
        return this.normalizeForSearch(cellVal).includes(this.normalizeForSearch(filterValue));
    }
  }

  private parseTimeToMinutes(val: unknown): number | null {
    if (val == null) return null;
    if (typeof val === 'number') return val >= 0 ? val : null;
    const str = String(val);
    const m = str.match(/^(\d{1,2}):(\d{2})/);
    if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    if (typeof val === 'string' || typeof val === 'number' || val instanceof Date) {
      const d = new Date(val as string | number | Date);
      return isNaN(d.getTime()) ? null : d.getHours() * 60 + d.getMinutes();
    }
    return null;
  }

  private parseTimeStringToMinutes(s: string): number | null {
    const m = String(s).trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  }

  onCellClick(col: TableColumnConfig<T>, row: T): void {
    // Comentario: se prioriza el click del CV para no romper el flujo existente,
    // incluso cuando la expansión por fila esté habilitada.
    if (col.key === 'cv') {
      this.cvClick.emit(row);
      return;
    }

    if (this.canToggleRowExpansion(col)) {
      this.toggleRowExpansion(row);
      return;
    }
  }
}
